import { getActiveAppCode } from "@/config/config";
import {
	AuthLoginDocument,
	type AuthLoginMutation,
	AuthRefreshTokenDocument,
	type AuthRefreshTokenMutation,
} from "@/generated/graphql";
import { urqlClient } from "../graphql/client";
import { createBrowserStorage } from "./storage";
import type { AuthStatus, Credentials, PermissionRule, Session } from "./types";

// Same key the previous auth layer used, so an existing login survives the swap.
const storage = createBrowserStorage("k3-auth");

type LoginPayload = NonNullable<AuthLoginMutation["authLogin"]>;
type RefreshPayload = NonNullable<AuthRefreshTokenMutation["authRefreshToken"]>;

// Only the permissions granted inside this app matter; a user may hold roles in
// several apps and the portal must not honour permissions from another one.
function permissionsOf(payload: LoginPayload | RefreshPayload): string[] {
	return (
		payload.appRolePermissions
			?.filter((app) => app?.appID === getActiveAppCode())
			?.flatMap(
				(app) =>
					app?.roles?.flatMap(
						(role) => role?.permissions?.map((permission) => permission.permissionID) ?? [],
					) ?? [],
			)
			?.filter((id): id is string => typeof id === "string") ?? []
	);
}

function sessionOf(payload: LoginPayload | RefreshPayload): Session {
	return {
		user: payload.user ?? null,
		accessToken: payload.accessToken,
		refreshToken: payload.refreshToken,
		permissions: permissionsOf(payload),
		meta: {
			branches: payload.branches ?? [],
			isAllBranches: payload.isAllBranches ?? false,
			defaultBranchID: payload.defaultBranchID,
			defaultBranchCode: payload.defaultBranchCode,
			defaultBranchName: payload.defaultBranchName,
			defaultRoleName: payload.defaultRoleName,
			defaultWarehouseID: payload.defaultWarehouseID,
			defaultWarehouseName: payload.defaultWarehouseName,
		},
	};
}

let session: Session | null = storage.read();
const listeners = new Set<() => void>();

function publish(next: Session | null): void {
	session = next;
	if (next) {
		storage.write(next);
	} else {
		storage.clear();
	}
	for (const listener of listeners) {
		listener();
	}
}

// Snapshot identity must be stable or useSyncExternalStore loops forever.
let snapshot: { status: AuthStatus; session: Session | null } = {
	status: session ? "authenticated" : "unauthenticated",
	session,
};

function refreshSnapshot(): void {
	snapshot = {
		status: session ? "authenticated" : "unauthenticated",
		session,
	};
}

export const authAdapter = {
	getState() {
		return snapshot;
	},

	getToken(): string | null {
		return session?.accessToken ?? null;
	},

	subscribe(listener: () => void): () => void {
		const wrapped = () => {
			refreshSnapshot();
			listener();
		};
		listeners.add(wrapped);
		return () => {
			listeners.delete(wrapped);
		};
	},

	can(rule: PermissionRule): boolean {
		const required = rule.permissions;
		if (required == null) return false;
		const granted = session?.permissions ?? [];
		const wanted = Array.isArray(required) ? required : [required];
		if (wanted.length === 0) return false;
		return rule.requireAll
			? wanted.every((permission) => granted.includes(permission))
			: wanted.some((permission) => granted.includes(permission));
	},

	async login(credentials: Credentials): Promise<Session> {
		const result = await urqlClient
			.mutation<AuthLoginMutation>(AuthLoginDocument, { input: credentials })
			.toPromise();
		if (result.error) throw result.error;

		const payload = result.data?.authLogin;
		if (!payload?.accessToken || !payload.user) {
			throw new Error("Invalid login response");
		}

		const next = sessionOf(payload);
		publish(next);
		refreshSnapshot();
		return next;
	},

	async refresh(): Promise<Session | null> {
		if (!session?.refreshToken) return null;
		const result = await urqlClient
			.mutation<AuthRefreshTokenMutation>(
				AuthRefreshTokenDocument,
				{ refreshToken: session.refreshToken },
				{ skipAuth: true },
			)
			.toPromise();

		if (result.error) {
			// An expired refresh token is terminal: drop everything and start over
			// at the login page rather than looping on a token the server rejects.
			authAdapter.expire();
			return null;
		}

		const payload = result.data?.authRefreshToken;
		if (!payload?.accessToken) {
			authAdapter.expire();
			return null;
		}

		const next = sessionOf(payload);
		publish(next);
		refreshSnapshot();
		return next;
	},

	// Replaces the session in place after a mutation that reissues tokens
	// (switching the default branch returns a fresh pair).
	adopt(payload: LoginPayload | RefreshPayload): void {
		publish(sessionOf(payload));
		refreshSnapshot();
	},

	async logout(): Promise<void> {
		publish(null);
		refreshSnapshot();
	},

	expire(): void {
		publish(null);
		refreshSnapshot();
		localStorage.clear();
		sessionStorage.clear();
		window.location.replace("/login");
	},
};

export type AuthAdapter = typeof authAdapter;
